import {Type} from 'class-transformer';
import {FileUploadRes} from '@yasc/yasc-material-components';
import {
    DistributionChannel,
    FBookingSourceType,
    FBookingSplitType,
    RecurringIntervals
} from '../../../enums/fibu.enums';
import {FinApiTransactionRes} from './finapi/finapi.transaction.contract';
import {FBookingAccountRes} from './misc.contracts';
import {RegexPatternReq} from '../core/regex-pattern';


export class BookingSplitReq {
    purpose: string = "";
    creditAccountId?: number;
    debitAccountId?: number;
    taxAccountId?: number;
    total: number;

    // frontend only
    creditAccountCode: string = "";
    debitAccountCode: string = "";

    _type: FBookingSplitType;

    constructor(type: FBookingSplitType, {
        creditAccountId = undefined,
        debitAccountId = undefined,
        taxAccountId = undefined,
        total = 0,
        purpose = "",
    }) {
        this.creditAccountId = creditAccountId;
        this.debitAccountId = debitAccountId;
        this.taxAccountId = taxAccountId;
        this.total = total;
        this.purpose = purpose;
        this._type = type;
    }

    static fromBookingSplitRes(copy: BookingSplitReq) {
        return new BookingSplitReq(copy._type, {
            // @ts-ignore
            creditAccountId: copy.creditAccountId,
            // @ts-ignore
            debitAccountId: copy.debitAccountId,
            purpose: copy.purpose,
            // @ts-ignore
            taxAccountId: copy.taxAccountId,
            total: copy.total,
        })
    }

    static fromBookingRes(copy: BookingRes): BookingSplitReq {
        let type = copy.debitAccountId != undefined ? FBookingSplitType.fibuSplitCredit : FBookingSplitType.fibuSplitDebit;
        return new BookingSplitReq(type, {
            // @ts-ignore
            creditAccountId: copy.creditAccountId,
            // @ts-ignore
            debitAccountId: copy.debitAccountId,
            total: copy.total,
            // @ts-ignore
            taxAccountId: copy.taxAccountId,
            purpose: copy.purpose
        });
    }
}

export class BookingReq {
    id?: number;
    receiptNumber?: string;
    purpose?: string;
    total!: number;
    amountType: DistributionChannel = DistributionChannel.standardBrutto;
    receiptDate: any = new Date();
    creditAccountId?: number = undefined;
    debitAccountId?: number = undefined;
    taxAccountId?: number = undefined;

    @Type(() => BookingSplitReq)
    splits: BookingSplitReq[] = [];

    // only be set if booking is recurring
    dayOfExecution?: number;
    recurringInterval?: RecurringIntervals;

    supportingDocuments: string[] = [];

    /**
     * This is only be set, if booking mask has preselected bank account.
     * Then we will only need creditor/debitor information. The role of creditor or debitor
     * is auto switched by backend depending on type
     */
    finApiAccountId?: number;


    // frontend only
    creditAccountCode?: string;
    debitAccountCode?: string;
    tax?: number;
    transactionId?: number;


    static fromBookingRes(res: BookingRes): BookingReq {
        let req = new BookingReq();
        req.id = res.id;
        req.receiptDate = res.receiptDate;
        req.creditAccountId = res.creditAccountId;
        req.debitAccountId = res.debitAccountId;
        req.receiptNumber = res.receiptNumber;
        req.taxAccountId = res.taxAccountId;
        req.purpose = res.purpose;
        req.amountType = res.amountType;
        req.total = res.total;
        req.splits = res.splits.map(e => BookingSplitReq.fromBookingSplitRes(e as BookingSplitReq))
        return req;
    }

    static fromTransaction(tx: FinApiTransactionRes): BookingReq {
        let req = new BookingReq();
        req.receiptDate = tx.bankBookingDate;
        req.purpose = tx.purpose;
        req.amountType = DistributionChannel.standardBrutto;
        req.total = tx.amount;
        req.transactionId = tx.id;
        req.finApiAccountId = tx.accountId;
        return req;
    }
}

export class BookingSplitRes {
    purpose: string = "";
    creditAccountId: number = 0;
    creditAccountCode: string = "";
    debitAccountId: number = 0;
    debitAccountCode: string = "";
    taxAccountId: number = 0;
    total: number = 0;

    static toSplitReq(res: BookingSplitRes): BookingSplitReq {
        let type = res.debitAccountId != undefined ? FBookingSplitType.fibuSplitCredit : FBookingSplitType.fibuSplitDebit;
        return new BookingSplitReq(type, {
            // @ts-ignore
            creditAccountId: res.creditAccountId,
            // @ts-ignore
            debitAccountId: res.debitAccountId,
            purpose: res.purpose,
            // @ts-ignore
            taxAccountId: res.taxAccountId,
            total: res.total,
        });
    }
}

export class BookingRes {
    id?: number;
    receiptNumber?: string;
    purpose!: string;
    creditAccountId?: number;
    creditAccountCode: string = "";
    debitAccountId?: number;
    debitAccountCode: string = "";
    total!: number;
    amountType: DistributionChannel = DistributionChannel.standardBrutto;
    receiptDate: any;
    createdDate: any;
    createdBy!: string;
    bookedBy?: string;
    dayOfExecution?: number;
    recurringInterval?: RecurringIntervals;
    taxAccountId?: number;
    supportingDocuments: FileUploadRes[] = [];
    source!: FBookingSourceType;
    isDraft: boolean = false;

    @Type(() => BookingSplitRes)
    splits: BookingSplitRes[] = [];

    @Type(() => FBookingValidationError)
    validationErrors: FBookingValidationError[]  = []
}

export class BookingImportTemplateRes {
    id!: number;
    created: any;
    name!: string;
    isGlobal: boolean = false;

    @Type(() => BookingImportTemplateTypeRes)
    mappings: BookingImportTemplateTypeRes[] = [];
}

export class BookingImportTemplateTypeRes {
    id!: number;
    target!: string;
    source!: string;
}

export class BookingCsvImportReq {
    mappingId!: number;
    data!: any[];

    constructor(mappingId: number, data: any[]) {
        this.mappingId = mappingId;
        this.data = data;
    }
}

export class BookJournalReq {
    ids: number [] = [];

    constructor(ids: number[]) {
        this.ids = ids;
    }
}

export class FibuAccountCreateReq {
    id?:number;
    code?: string;
    name?: string;
    categoryId?: number;
    receiptNumberPatterns: RegexPatternReq[] = [];
    taxAccountId?: number;

    constructor(item?: Partial<FibuAccountCreateReq>) {
        this.id = item?.id;
        this.code = item?.code;
        this.name = item?.name;
        this.categoryId = item?.categoryId;
        this.receiptNumberPatterns = item?.receiptNumberPatterns ?? [];
        this.taxAccountId = item?.taxAccountId;
    }

    static fromRes($event: FBookingAccountRes) {
        return new FibuAccountCreateReq({
            id: $event.id,
            name: $event.name,
            code: $event.code,
            categoryId: $event.category.id,
            taxAccountId: $event.taxAccount?.id,
            receiptNumberPatterns: $event.receiptNumberPatterns ?? []
        })
    }
}

export class FBookingValidationError {
    memberNames?:string[];
    errorMessage?:string;

    getMembers():string{
        return this.memberNames?.join(' & ') ?? '';
    }
}

export class FibuOcrFileReq {
    fileIds: string[] = [];

    constructor(fileIds: string[]) {
        this.fileIds = fileIds;
    }
}

export class FibuOcrFileRes {
    fileName!: string;
    booked!: boolean;
    booking!: BookingReq;
}